import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

const METRICS = [
  { key: 'bets',  label: 'Bets',     slope: 'bet_count_slope', ratio: 'recent_vs_prior_bets_ratio',  h2h1: 'h2_minus_h1_bets',  avg: 'avg_bets_per_day'  },
  { key: 'stake', label: 'Turnover', slope: 'stake_slope',     ratio: 'recent_vs_prior_stake_ratio', h2h1: 'h2_minus_h1_stake', avg: 'avg_stake_per_day' },
  { key: 'ggr',   label: 'GGR',      slope: 'ggr_slope',       ratio: 'recent_vs_prior_ggr_ratio',   h2h1: 'h2_minus_h1_ggr',   avg: 'avg_ggr_per_day'   },
];

const MEASURES = [
  {
    name: 'Slope',
    desc: 'Linear trend fitted across all 180 days. Positive = trending up, negative = trending down. Smooth, but one big day can tilt it.',
  },
  {
    name: 'Recent ÷ prior',
    desc: 'Last 28 days (days 152–179) ÷ prior 152 days. Above 1 = running above the player\'s own baseline right now, below 1 = tailing off. The most current signal, but noisy for sparse players.',
  },
  {
    name: 'H2 − H1',
    desc: 'Second half of the window minus the first half (days 90–179 vs 0–89), in absolute units. Coarse, but resistant to outliers.',
  },
];

const DIR_STYLE = {
  up:   { icon: '↑', color: '#00c896' },
  down: { icon: '↓', color: '#e05555' },
  flat: { icon: '–', color: '#7a8499' },
};

function fmtNum(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1000000) return (n / 1000000).toFixed(2) + 'M';
  if (abs >= 1000) return (n / 1000).toFixed(1) + 'K';
  if (abs >= 10) return Math.round(n).toLocaleString('en-GB');
  return n.toFixed(2);
}

const fmtSigned = (n) => (n != null && n >= 0 ? '+' : '') + fmtNum(n);

function slopeDir(slope) {
  if (slope > 0) return 'up';
  if (slope < 0) return 'down';
  return 'flat';
}

function ratioDir(r) {
  if (r > 1.1) return 'up';
  if (r < 0.9) return 'down';
  return 'flat';
}

function h2h1Dir(diff, avg) {
  const threshold = 0.1 * Math.abs(avg || 0);
  if (diff > threshold) return 'up';
  if (diff < -threshold) return 'down';
  return 'flat';
}

function Cell({ dir, value, hint }) {
  const dirStyle = DIR_STYLE[dir];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-baseline justify-end gap-1 py-[5px]">
          <span className="text-[13px] font-bold leading-none" style={{ color: dirStyle.color }}>
            {dirStyle.icon}
          </span>
          <span className="text-[12px] text-ink">{value}</span>
        </div>
      </TooltipTrigger>
      {hint && <TooltipContent side="top">{hint}</TooltipContent>}
    </Tooltip>
  );
}

const headClass = 'text-[10px] font-bold uppercase tracking-[0.4px] text-right text-ink-dim';

export default function TrajectorySummaryCard({ trajectory }) {
  return (
    <div className="rounded border border-white/10 bg-surface-1 p-4">
      <p className="text-[14px] font-bold mb-0.5 text-ink">
        Trajectory summary
      </p>
      <p className="text-[11px] leading-snug text-ink-dim mb-[6px]">
        Three measures per metric — they're meant to disagree. Recent-vs-prior compares the last 28
        days to the prior 152.
      </p>

      {!trajectory ? (
        <p className="text-[12px] text-ink-dim">
          No trajectory features available for this player.
        </p>
      ) : (
        <>
          <div
            className="grid gap-1 items-center"
            style={{ gridTemplateColumns: '70px 1fr 1fr 1fr' }}
          >
            <div />
            <span className={headClass}>Slope</span>
            <span className={headClass}>Recent÷prior</span>
            <span className={headClass}>H2−H1</span>

            {METRICS.map((m) => {
              const slope = trajectory[m.slope];
              const ratio = trajectory[m.ratio];
              const h2h1 = trajectory[m.h2h1];
              const avg = trajectory[m.avg];

              const dS = slopeDir(slope);
              const dR = ratioDir(ratio);
              const dH = h2h1Dir(h2h1, avg);
              const mixed = [dS, dR, dH].includes('up') && [dS, dR, dH].includes('down');

              return (
                <React.Fragment key={m.key}>
                  <div className="flex items-center gap-1">
                    <span className="text-[12px] font-bold text-ink">
                      {m.label}
                    </span>
                    {mixed && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-[9px] cursor-help text-warning">
                            ⚠
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          The three measures disagree — e.g. trending up over the half but dropping off in the last month.
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <Cell
                    dir={dS}
                    value={fmtSigned(slope)}
                    hint="Linear regression slope per day. Positive = trending up, negative = trending down."
                  />
                  <Cell
                    dir={dR}
                    value={ratio != null ? ratio.toFixed(2) + '×' : '—'}
                    hint="Avg of last 28 days ÷ avg of days 0–151. >1.1 up, <0.9 down."
                  />
                  <Cell
                    dir={dH}
                    value={fmtSigned(h2h1)}
                    hint="Avg of days 90–179 minus avg of days 0–89 (absolute units)."
                  />
                </React.Fragment>
              );
            })}
          </div>

          <div className="flex flex-col gap-[6px] mt-[6px] pt-[5px] border-t border-white/5">
            {MEASURES.map((m) => (
              <p key={m.name} className="text-[11px] leading-[1.45] text-ink-dim">
                <span className="font-bold text-ink">{m.name}</span>
                {' — '}{m.desc}
              </p>
            ))}
            <p className="text-[10.5px] italic mt-0.5 leading-[1.45] text-ink-dim">
              The ⚠ flag marks a metric where the three measures disagree — e.g. a positive slope but
              recent÷prior below 1 means the player trended up across the half but dropped off in the
              last month. That contradiction is the signal.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
